import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onVerified: (phoneE164: string) => void;
  /** Optional pre-filled phone (E.164 or raw). */
  defaultPhone?: string | null;
  /** Called when the dialog closes without a successful verification. */
  onCancel?: () => void;
}

function normalizeUsPhone(raw: string): string | null {
  const digits = raw.replace(/\D+/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return null;
}

function formatUsPhone(e164OrRaw: string | null | undefined): string {
  if (!e164OrRaw) return "";
  const d = e164OrRaw.replace(/\D+/g, "");
  const ten = d.length === 11 && d.startsWith("1") ? d.slice(1) : d;
  if (ten.length !== 10) return e164OrRaw;
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
}

export function PhoneVerificationDialog({ open, onOpenChange, onVerified, defaultPhone, onCancel }: Props) {
  const { user } = useAuth();
  const [step, setStep] = useState<"enter" | "verify">("enter");
  const [phone, setPhone] = useState(formatUsPhone(defaultPhone));
  const [otp, setOtp] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifiedOnce, setVerifiedOnce] = useState(false);
  const autoSentRef = useRef(false);

  const reset = () => {
    setStep("enter");
    setPhone(formatUsPhone(defaultPhone));
    setOtp("");
    setVerifiedOnce(false);
    autoSentRef.current = false;
  };

  const handleSend = async () => {
    if (!user) {
      toast.error("You must be signed in to verify a phone number");
      return;
    }
    const e164 = normalizeUsPhone(phone);
    if (!e164) {
      toast.error("Enter a valid US mobile number");
      return;
    }
    setSending(true);
    try {
      // Generate 6-digit code, persist in phone_otps, then send via Twilio.
      // user_id is required by RLS: WITH CHECK (auth.uid() = user_id).
      const code = String(Math.floor(100000 + Math.random() * 900000));
      const expiresAt = new Date(Date.now() + 10 * 60 * 1000).toISOString();
      const { error: insErr } = await supabase.from("phone_otps").insert({
        user_id: user.id,
        phone: e164,
        otp_code: code,
        expires_at: expiresAt,
      } as any);
      if (insErr) throw insErr;
      const { error: smsErr } = await supabase.functions.invoke("send-sms-otp", {
        body: { phone: e164, otp: code },
      });
      if (smsErr) throw smsErr;
      toast.success("Code sent");
      setStep("verify");
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to send code");
    } finally {
      setSending(false);
    }
  };

  // Auto-send on open when a valid pre-filled phone is provided — no redundant
  // re-entry after the consent modal already collected/confirmed the number.
  useEffect(() => {
    if (!open) return;
    if (autoSentRef.current) return;
    if (!user) return;
    const e164 = normalizeUsPhone(phone);
    if (!e164) return;
    autoSentRef.current = true;
    void handleSend();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user]);



  const handleVerify = async () => {
    if (!user) return;
    const e164 = normalizeUsPhone(phone);
    if (!e164 || otp.length !== 6) return;
    setVerifying(true);
    try {
      // Use the same server-side RPC the signup flow uses. It picks the most
      // recent unverified OTP for this user, enforces 5-attempt lockout,
      // checks expiry, flips phone_otps.verified + profiles.phone_verified.
      const { data, error } = await supabase.rpc("verify_phone_otp", {
        p_user_id: user.id,
        p_otp_code: otp,
      });
      if (error) throw new Error(error.message || "Verification failed");
      const result = (data ?? {}) as { success?: boolean; message?: string };
      if (!result.success) {
        toast.error(result.message || "Invalid or expired code");
        return;
      }

      // Persist the phone number itself on the profile (RPC only sets the flag).
      const { error: upErr } = await supabase
        .from("profiles")
        .update({ phone: e164 } as any)
        .eq("id", user.id);
      if (upErr) throw upErr;

      toast.success("Phone verified");
      setVerifiedOnce(true);
      onVerified(e164);
      onOpenChange(false);
      reset();
    } catch (e: any) {
      toast.error(e?.message ?? "Verification failed");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) { if (!verifiedOnce) onCancel?.(); reset(); } }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Verify your phone</DialogTitle>
          <DialogDescription>
            We'll text a 6-digit code to confirm this number before sending SMS alerts.
          </DialogDescription>
        </DialogHeader>
        {step === "enter" ? (
          <div className="space-y-3">
            <Label>Mobile number (US)</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 123-4567" />
            <p className="text-xs text-muted-foreground">Msg & data rates may apply. Reply STOP at any time.</p>
          </div>
        ) : (
          <div className="space-y-3">
            <Label>Enter the 6-digit code</Label>
            <Input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="123456" inputMode="numeric" />
            <button className="text-xs text-brand-teal hover:underline" onClick={handleSend} disabled={sending}>
              Resend code
            </button>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          {step === "enter" ? (
            <Button onClick={handleSend} disabled={sending}>{sending ? "Sending..." : "Send code"}</Button>
          ) : (
            <Button onClick={handleVerify} disabled={verifying || otp.length !== 6}>{verifying ? "Verifying..." : "Verify"}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
